import * as React from "react";
import MuiTabs from "@material-ui/core/Tabs";
import { TabHeight } from "../Tab";
import type { TabValue } from "../Tab";
import type { UiBaseProps } from "../uiProps.utils";

export enum TabsLayout {
  // Each tab as wide as its label, scrolling sideways when they overflow.
  SCROLLABLE = "scrollable",
  // The tabs share the full width equally.
  FULL_WIDTH = "fullWidth",
  STANDARD = "standard",
}

export enum TabsMaxWidth {
  // A short row of sections (2–3 tabs): keeps its width and stays centred on wide screens.
  COMPACT = "30rem",
}

export type TabsProps<T extends TabValue> = UiBaseProps & {
  value: T;
  onChange: (value: T) => void;
  layout?: TabsLayout;
  // Arrow buttons at the ends of a scrollable row; off, the row is swiped.
  showScrollButtons?: boolean;
  // Caps the row and centres it, so the tabs do not stretch across a wide screen.
  maxWidth?: TabsMaxWidth;
  // Give each Tab the same height.
  height?: TabHeight;
};

// Orange label and underline on the selected tab, as everywhere in the app.
export const Tabs = <T extends TabValue>({
  onChange,
  layout = TabsLayout.STANDARD,
  showScrollButtons = false,
  maxWidth,
  height,
  style,
  ...rest
}: TabsProps<T>) => (
  <MuiTabs
    variant={layout}
    scrollButtons={showScrollButtons ? "auto" : "off"}
    indicatorColor="primary"
    textColor="primary"
    onChange={(_event, next: T) => onChange(next)}
    style={{
      ...(maxWidth && { maxWidth, marginLeft: "auto", marginRight: "auto" }),
      ...(height && { minHeight: height }),
      ...style,
    }}
    {...rest}
  />
);
